import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Shield, User, Users } from "lucide-react";
import { demoLogin, type DemoPersona } from "@/api/auth";
import { useAuth } from "@/hooks/useAuth";

interface PersonaCard {
  key: DemoPersona;
  title: string;
  description: string;
  icon: typeof Shield;
}

const CARDS: PersonaCard[] = [
  {
    key: "hr_admin",
    title: "HR Admin",
    description:
      "Full access to all modules, AI agents, settings, reports, and compliance tools.",
    icon: Shield,
  },
  {
    key: "manager",
    title: "Manager",
    description:
      "Team dashboard, leave approvals, 1:1 notes, and direct report management.",
    icon: Users,
  },
  {
    key: "employee",
    title: "Employee",
    description:
      "Self-service PTO requests, document signing, company directory, and profile.",
    icon: User,
  },
  {
    key: "executive",
    title: "Executive",
    description:
      "AI-powered briefings, org-wide analytics, reports, and strategic dashboards.",
    icon: BarChart3,
  },
];

const LANDINGS: Record<DemoPersona, string> = {
  hr_admin: "/",
  manager: "/my/team",
  employee: "/my-documents",
  executive: "/agents/atlas",
};

const BRAND_BLUE = "#1B18FF";

export function DemoPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState<DemoPersona | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(
    async (persona: DemoPersona, redirect?: string | null) => {
      setLoading(persona);
      setError(null);
      try {
        const res = await demoLogin(persona);
        setSession(res.access_token, res.user);
        window.dispatchEvent(new Event("demo:session"));
        // Mark the tour as eligible for this session (the first AppShell
        // render will pick it up). Not stored beyond the session.
        sessionStorage.setItem("lenavio.demoTour.pending", persona);
        navigate(redirect || LANDINGS[persona]);
      } catch (e: unknown) {
        // demoLogin (native fetch) throws Error with the backend detail
        // string; axios shape kept as fallback for belt-and-suspenders.
        const msg =
          (e as Error)?.message ||
          (e as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ||
          "Couldn't start the demo. Please try again.";
        setError(typeof msg === "string" ? msg : "Couldn't start the demo.");
      } finally {
        setLoading(null);
      }
    },
    [navigate, setSession]
  );

  // Deep-link support: /demo?persona=manager&redirect=/my/team auto-logs in.
  useEffect(() => {
    const p = params.get("persona") as DemoPersona | null;
    const redirect = params.get("redirect");
    if (p && (["hr_admin", "manager", "employee", "executive"] as const).includes(p)) {
      void start(p, redirect);
    }
    // Only on mount — `params` reference changes would re-trigger and loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="flex items-center justify-between px-6 py-4">
        <img
          src="/favicon-512.png"
          alt="Lenavio"
          className="h-10 w-10 rounded-md"
        />
        <a
          href="https://lenavio.com"
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← Back to lenavio.com
        </a>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col items-center px-6 py-10 sm:py-16">
        <h1
          className="text-center text-4xl font-bold tracking-tight sm:text-5xl"
          style={{ color: BRAND_BLUE }}
        >
          See Lenavio in Action
        </h1>
        <p className="mt-3 text-center text-lg text-gray-600">
          Choose your role to explore the platform. No signup required.
        </p>

        {error && (
          <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-10 grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            const isLoading = loading === card.key;
            return (
              <motion.button
                key={card.key}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: i * 0.05 }}
                type="button"
                disabled={isLoading}
                onClick={() => start(card.key)}
                className="group flex flex-col items-start gap-4 rounded-2xl border border-gray-200 bg-white p-6 text-left shadow-sm transition hover:border-[color:var(--brand)] hover:shadow-md disabled:opacity-60"
                style={{ ["--brand" as string]: BRAND_BLUE }}
              >
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {card.title}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {card.description}
                  </p>
                </div>
                <span
                  className="mt-auto text-sm font-semibold"
                  style={{ color: BRAND_BLUE }}
                >
                  {isLoading ? "Starting…" : `Explore as ${card.title} →`}
                </span>
              </motion.button>
            );
          })}
        </div>

        <p className="mt-12 text-center text-xs text-gray-500">
          30-minute session · Read-only mode · No data is saved
        </p>
      </main>
    </div>
  );
}

export default DemoPage;
